import { useEffect, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { MapContainer, TileLayer, ImageOverlay, Marker, Popup, CircleMarker, Polyline, Tooltip, ZoomControl, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { formatDistanceToNow } from "date-fns";
import { buildOverlay, gridBounds, valueOf, riskLevel } from "@/lib/params";
import { useI18n } from "@/lib/i18n";
import { REPORT_TYPES } from "@/lib/reportTypes";

const iconCache = {};
const reportIcon = (type) => {
  if (iconCache[type]) return iconCache[type];
  const { color, icon: Icon } = REPORT_TYPES[type];
  const svg = renderToStaticMarkup(<Icon size={15} color="#fff" strokeWidth={2.4} />);
  iconCache[type] = L.divIcon({
    className: "",
    html: `<div class="report-pin ${type === "sos" ? "report-pin-sos" : ""}" style="background:${color}">${svg}</div>`,
    iconSize: [30, 30], iconAnchor: [15, 15],
  });
  return iconCache[type];
};

const labelIcon = (text, dir) =>
  L.divIcon({
    className: "",
    html: `<div class="grid-label">${dir != null ? `<span class="grid-arrow" style="transform:rotate(${dir + 180}deg)">↑</span>` : ""}${text}</div>`,
    iconSize: [48, 18], iconAnchor: [24, 9],
  });

const Events = ({ onSelect, onMove }) => {
  useMapEvents({
    click: (e) => onSelect({ lat: e.latlng.lat, lon: e.latlng.lng }),
    moveend: (e) => { const c = e.target.getCenter(); onMove({ lat: c.lat, lon: c.lng }); },
  });
  return null;
};

const FlyTo = ({ target }) => {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo([target.lat, target.lon], 8, { duration: 1.2 }); }, [target, map]);
  return null;
};

const GridLabels = ({ grid, param, t }) => {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useMapEvents({ zoomend: () => setZoom(map.getZoom()) });
  const stride = zoom >= 7 ? 1 : 2;
  const cols = grid.lons.length;
  const vals = grid.data[param.key][t];
  const dirs = param.dirKey ? grid.data[param.dirKey][t] : null;
  return vals.map((raw, i) => {
    const v = valueOf(param, raw);
    const ri = Math.floor(i / cols), ci = i % cols;
    if (v == null || ri % stride || ci % stride || (["rain", "hail", "snow", "snowdepth"].includes(param.id) && v < 0.05)) return null;
    const lat = grid.lats[ri], lon = grid.lons[ci];
    return <Marker key={i} position={[lat, lon]} interactive={false} icon={labelIcon(v.toFixed(param.digits), dirs ? dirs[i] : null)} />;
  });
};

const ReportMarkers = ({ reports, onConfirm }) => {
  const { t, locale } = useI18n();
  return reports.map((r) => (
    <Marker key={r.id} position={[r.lat, r.lon]} icon={reportIcon(r.type)}>
      <Popup>
        <div className="space-y-1" data-testid={`report-popup-${r.id}`}>
          <div className="font-heading text-base font-bold" style={{ color: REPORT_TYPES[r.type].color }}>{t(REPORT_TYPES[r.type].label)}</div>
          {r.note && <div className="text-sm text-slate-200">{r.note}</div>}
          <div className="font-mono text-[11px] text-slate-400">
            {formatDistanceToNow(new Date(r.created_at.endsWith("Z") || r.created_at.includes("+") ? r.created_at : r.created_at + "Z"), { addSuffix: true, locale })} · {r.confirmations} {t("onay")}
          </div>
          <button data-testid={`report-confirm-${r.id}`} disabled={r.mine || r.confirmed} onClick={() => onConfirm(r.id)}
            className="mt-1 rounded-full bg-sky-500/20 px-3 py-1 text-xs font-semibold text-sky-300 hover:bg-sky-500/30 disabled:cursor-not-allowed disabled:bg-white/5 disabled:text-slate-500">
            {r.mine ? t("Sizin raporunuz") : r.confirmed ? t("✓ Onayladınız") : t("Hâlâ geçerli, onayla")}
          </button>
        </div>
      </Popup>
    </Marker>
  ));
};

const FitRoute = ({ data }) => {
  const map = useMap();
  useEffect(() => {
    if (data) map.flyToBounds(data.segments.flatMap((s) => s.coords), { paddingTopLeft: window.innerWidth > 640 ? [360, 90] : [30, 90], paddingBottomRight: [60, 140], duration: 1 });
  }, [data, map]);
  return null;
};

const RouteLayer = ({ data, onSelect }) => (
  <>
    {data.segments.map((s, i) => (
      <Polyline key={`b${i}`} positions={s.coords} pathOptions={{ color: "#020617", weight: 9, opacity: 0.6 }} />
    ))}
    {data.segments.map((s, i) => (
      <Polyline key={i} positions={s.coords} pathOptions={{ color: riskLevel(s.risk).color, weight: 5 }} />
    ))}
    {data.samples.map((s) => (
      <CircleMarker key={s.km} center={[s.lat, s.lon]} radius={5} eventHandlers={{ click: () => onSelect(s) }}
        pathOptions={{ color: "#fff", weight: 2, fillColor: riskLevel(s.road).color, fillOpacity: 1 }}>
        <Tooltip direction="top">{Math.round(s.km)} km · {s.time.slice(11, 16)} · {Math.round(s.temperature_2m)}°</Tooltip>
      </CircleMarker>
    ))}
  </>
);

const userIcon = (heading) => L.divIcon({
  className: "",
  html: `<div class="user-pin"><div class="user-arrow" style="transform:rotate(${heading || 0}deg)"></div></div>`,
  iconSize: [34, 34], iconAnchor: [17, 17],
});

const FollowUser = ({ pos, follow, setFollow }) => {
  const map = useMap();
  useMapEvents({ dragstart: () => setFollow(false) });
  useEffect(() => {
    if (pos && follow) map.setView([pos.lat, pos.lon], Math.max(map.getZoom(), 15), { animate: true });
  }, [pos, follow, map]);
  return null;
};

const TILES = {
  dark: ["Canvas/World_Dark_Gray_Base", "Canvas/World_Dark_Gray_Reference"],
  light: ["Canvas/World_Light_Gray_Base", "Canvas/World_Light_Gray_Reference"],
};
const tileUrl = (p) => `https://server.arcgisonline.com/ArcGIS/rest/services/${p}/MapServer/tile/{z}/{y}/{x}`;

export const MapView = ({ grid, param, t, layers, reports, selected, onSelect, onMove, flyTarget, onConfirm, routeData, theme, nav }) => {
  const url = useMemo(() => (grid ? buildOverlay(grid, param, t) : null), [grid, param, t]);
  const bounds = grid && gridBounds(grid);
  return (
    <div className="absolute inset-0" data-testid="weather-map">
      <MapContainer center={[39.0, 35.2]} zoom={6} minZoom={4} zoomControl={false} className="h-full w-full" worldCopyJump>
        <TileLayer key={`b-${theme}`} url={tileUrl(TILES[theme][0])} maxNativeZoom={16} attribution='Tiles &copy; Esri | Open-Meteo | OSRM' />
        {url && <ImageOverlay url={url} bounds={bounds} opacity={0.85} className="weather-overlay" />}
        <TileLayer key={`r-${theme}`} url={tileUrl(TILES[theme][1])} maxNativeZoom={16} zIndex={650} pane="shadowPane" />
        {routeData && <RouteLayer data={routeData} onSelect={onSelect} />}
        {!nav?.active && <FitRoute data={routeData} />}
        {nav?.active && nav.pos && <Marker position={[nav.pos.lat, nav.pos.lon]} icon={userIcon(nav.pos.heading)} interactive={false} zIndexOffset={1000} />}
        {nav?.active && <FollowUser pos={nav.pos} follow={nav.follow} setFollow={nav.setFollow} />}
        {grid && layers.values && <GridLabels grid={grid} param={param} t={t} />}
        {layers.reports && <ReportMarkers reports={reports} onConfirm={onConfirm} />}
        {selected && <CircleMarker center={[selected.lat, selected.lon]} radius={9} pathOptions={{ color: "#38BDF8", weight: 2, fillOpacity: 0.25 }} />}
        <Events onSelect={onSelect} onMove={onMove} />
        <FlyTo target={flyTarget} />
        <ZoomControl position="bottomright" />
      </MapContainer>
    </div>
  );
};
